'use client'
import React, { useState, useEffect } from 'react'
import { socket } from '../Containers/MainContainer'
import Skeleton from 'react-loading-skeleton'
import { Link } from 'react-router-dom'
import { ArrowUp, ArrowDown, Layers, Users, DollarSign, TrendingUp, AlertCircle, Activity, Users2, Gamepad, Target, TrendingDown, Flame, CheckCircle, XCircle } from 'lucide-react'
import { numberWithCommas } from '../utils'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'

// Reusable table section: card + title + table + skeleton
const DataTable = ({ icon: Icon, iconColor, title, headers, data, renderRow, skeletonRows = 5 }) => (
  <div className="section-card">
    <h3 className="section-title">
      <Icon style={{ color: iconColor }} />
      {title}
    </h3>
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className={h.align === 'right' ? 'text-right' : ''}>{h.label || h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!data ? (
            Array(skeletonRows).fill().map((_, i) => (
              <tr key={i}>
                {headers.map((_, j) => <td key={j}><Skeleton height={20} baseColor="#161b22" highlightColor="#21262d" /></td>)}
              </tr>
            ))
          ) : (
            data.map(renderRow)
          )}
        </tbody>
      </table>
    </div>
  </div>
)

const StatCard = ({ title, value, icon: Icon, change, color = 'blue', isLoading, subtitle, progress }) => (
  <div className={`metric-banner metric-banner--${color}`}>
    {isLoading ? (
      <Skeleton height={80} width="100%" baseColor="#161b22" highlightColor="#21262d" />
    ) : (
      <>
        <div className="metric-banner-row">
          <div className="metric-banner-left">
            <div className={`stat-icon stat-icon--${color}`}><Icon /></div>
            <div>
              <div className="stat-label">{title}</div>
              {subtitle && <div className="metric-banner-sub">{subtitle}</div>}
            </div>
          </div>
          <div className="metric-banner-right">
            <div className={`metric-banner-value metric-banner-value--${color}`}>{value}</div>
            {change !== undefined && (
              <div className={`stat-change ${change >= 0 ? 'stat-change--up' : 'stat-change--down'}`}>
                {change >= 0 ? <ArrowUp /> : <ArrowDown />}
                <span>{Math.abs(change).toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
        {progress !== undefined && (
          <div className="metric-banner-bar">
            <div className={`metric-banner-fill metric-banner-fill--${color}`} style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
        )}
      </>
    )}
  </div>
)

function PlutoMain() {
  const [d, setD] = useState({
    activeUsers: undefined, revenue: undefined, spendings: undefined, profits: undefined,
    monthlyRev: undefined, monthlyTarget: undefined, monthlyRevThis: undefined, thisMonthTarget: undefined,
    top10Offers: undefined, last10Offers: undefined, last10Users: undefined, last10OffersHours:undefined,
    revt: undefined, withT: undefined, dataWalls: undefined, dataWallsX: undefined, loading: true
  })

  const dayLabel = (daysBack) => {
    const dt = new Date(); dt.setDate(dt.getDate() - daysBack)
    return dt.toLocaleDateString('en-US', { weekday: 'long' })
  }
  const monthLabel = (monthsBack) => {
    const dt = new Date(); dt.setMonth(dt.getMonth() - monthsBack)
    return dt.toLocaleDateString('en-US', { month: 'long' })
  }

  const getProgress = () => {
    const target = d.thisMonthTarget || 0
    const now = new Date()
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
    const daysPassed = now.getDate()
    const daysLeft = daysInMonth - daysPassed
    const rev = d.monthlyRevThis || 0
    const shouldHave = (target / daysInMonth) * daysPassed
    const dailyNeeded = daysLeft > 0 ? (target - rev) / daysLeft : 0
    const pct = target > 0 ? (rev / target) * 100 : 0
    const completedDays = daysPassed - 1
    const completedRev = rev - (d.revt || 0)
    const dailyRate = completedDays > 0 ? completedRev / completedDays : rev
    const estimated = dailyRate * daysInMonth
    const estimatedPct = target > 0 ? (estimated / target) * 100 : 0
    return { target, rev, shouldHave, dailyNeeded, pct, daysPassed, daysLeft, daysInMonth, onTrack: rev >= shouldHave, dailyRate, estimated, estimatedPct }
  }

  useEffect(() => {
    socket.emit('getHomeAdmin')

    socket.on('response_get_home_admin_data', (data) => {
      let cc = data.dataWallsX
      for (let i = 0; i < data.dataWalls.length; i++) {
        const w = data.dataWalls[i]
        const match = cc.find(x => x.wall_name === w.wall_name)
        if (!match) { cc.push({ ...w, usd2: 0 }) }
        else { match.usd2 = match.usd; match.usd = w.usd }
      }
      setD(prev => ({ ...prev,
        top10Offers: data.top10OffersMadeToday,
        thisMonthTarget: parseFloat(data.monthlyTarget[data.monthlyTarget.length - 1]),
        monthlyRevThis: parseFloat(data.monthlyRev[data.monthlyRev.length - 1]),
        last10Offers: data.last10offers, last10Users: data.last10Users, last10OffersHours: data.last10OffersHours,
        monthlyRev: data.monthlyRev, monthlyTarget: data.monthlyTarget,
        revt: data.dailyR, withT: data.dailyW, dataWallsX: cc, dataWalls: data.dataWalls,
      }))
    })

    socket.on('response_get_home_admin_data_activeusers', (data) => {
      setD(prev => ({ ...prev, activeUsers: data.activeUsers }))
    })
    socket.on('response_get_home_admin_data_activeusersmonthly', (data) => {
      setD(prev => ({ ...prev, activeUsersMonthly: data.activeUsersMonthly }))
    })
    socket.on('response_get_home_admin_data_dailyrev', (data) => {
      setD(prev => ({ ...prev, revenue: data.dataRev, profits: data.profits, spendings: data.dataWith, loading: false }))
    })

    return () => {
      socket.off('response_get_home_admin_data')
      socket.off('response_get_home_admin_data_activeusers')
      socket.off('response_get_home_admin_data_activeusersmonthly')
      socket.off('response_get_home_admin_data_dailyrev')
    }
  }, [])

  const fmt = (n) => n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' : n
  const pct = (val, total) => total > 0 ? ((val / total) * 100).toFixed(1) : '0.0'
  const chg = (arr, current) => arr && arr.length > 1 ? ((current / (arr[arr.length - 2] || 1)) - 1) * 100 : 0

  const p = getProgress()

  // Prepare table datasets
  const revenueRows = d.revenue?.map((val, i) => ({
    date: dayLabel(d.revenue.length - 1 - i), revenue: val,
    spendings: d.spendings?.[i] || 0, profits: d.profits?.[i] || 0,
    margin: val > 0 ? ((d.profits?.[i] || 0) / val * 100) : 0
  })).slice(-10).reverse()

  const monthlyRows = d.monthlyRev?.map((val, i) => ({
    month: monthLabel(d.monthlyRev.length - 1 - i), revenue: val,
    target: d.thisMonthTarget || 0, achievement: pct(val, d.thisMonthTarget || 1),
    diff: val - (d.thisMonthTarget || 0)
  })).slice(-12).reverse()

  const wallRows = d.dataWallsX?.map(w => ({
    name: w.wall_name, today: w.usd2 || 0, todayPct: pct(w.usd2 || 0, 167),
    monthly: w.usd, monthlyPct: pct(w.usd, 5000)
  })).reverse()

  const chartData = d.activeUsers?.map((val, i) => ({ date: dayLabel(6 - i), users: val }))

  const pctColor = (v) => v >= 100 ? 'cell-green' : v >= 60 ? 'cell-yellow' : 'cell-red'
  const dailyPct = p.dailyNeeded > 0 ? ((d.revt || 0) / p.dailyNeeded) * 100 : 0

  return (
    <div style={{ color: '#e6edf3' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>

        {/* Header */}
        <div className="dashboard-header" style={{ marginBottom: '24px' }}>
          <h1>Analytics Dashboard</h1>
          <p className="subtitle">Revenue overview and performance metrics</p>
          <div className="date">
            Today: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* === TODAY'S DAILY TARGET STATUS === */}
        {d.revenue && (
          <div style={{
            marginBottom: '24px',
            background: dailyPct >= 100 ? 'rgba(63,185,80,0.08)' : dailyPct >= 50 ? 'rgba(210,153,34,0.08)' : 'rgba(248,81,73,0.08)',
            border: `1px solid ${dailyPct >= 100 ? 'rgba(63,185,80,0.3)' : dailyPct >= 50 ? 'rgba(210,153,34,0.3)' : 'rgba(248,81,73,0.3)'}`,
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {dailyPct >= 100
                  ? <CheckCircle style={{ width: '28px', height: '28px', color: '#3fb950' }} />
                  : <Flame style={{ width: '28px', height: '28px', color: dailyPct >= 50 ? '#d29922' : '#f85149' }} />
                }
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#e6edf3', margin: 0 }}>
                    {dailyPct >= 100 ? 'Daily Target Hit!' : dailyPct >= 50 ? 'Keep Pushing - Halfway There' : 'Daily Target At Risk'}
                  </h3>
                  <p style={{ color: '#8b949e', fontSize: '13px', margin: '2px 0 0' }}>
                    Need <span style={{ color: '#f97316', fontWeight: 600 }}>${numberWithCommas(p.dailyNeeded.toFixed(0))}</span>/day to stay on track for the monthly target
                  </p>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: dailyPct >= 100 ? '#3fb950' : dailyPct >= 50 ? '#d29922' : '#f85149' }}>
                  {dailyPct.toFixed(0)}%
                </div>
                <div style={{ fontSize: '12px', color: '#8b949e' }}>of daily target</div>
              </div>
            </div>
            {/* Daily progress bar */}
            <div style={{ background: '#21262d', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '6px', transition: 'width 0.5s',
                width: `${Math.min(100, dailyPct)}%`,
                background: dailyPct >= 100 ? '#3fb950' : dailyPct >= 50 ? '#d29922' : '#f85149'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '13px' }}>
              <span style={{ color: '#8b949e' }}>
                Today: <span style={{ color: '#e6edf3', fontWeight: 600 }}>${numberWithCommas(d.revt || 0)}</span>
              </span>
              <span style={{ color: '#8b949e' }}>
                Remaining: <span style={{ color: dailyPct >= 100 ? '#3fb950' : '#f97316', fontWeight: 600 }}>
                  {dailyPct >= 100 ? 'Done!' : `$${numberWithCommas(Math.max(0, p.dailyNeeded - (d.revt || 0)).toFixed(0))}`}
                </span>
              </span>
              <span style={{ color: '#8b949e' }}>
                Target: <span style={{ color: '#e6edf3', fontWeight: 600 }}>${numberWithCommas(p.dailyNeeded.toFixed(0))}</span>
              </span>
            </div>
          </div>
        )}

        {/* === MONTHLY TARGET PROGRESS === */}
        <div style={{
          marginBottom: '24px',
          background: p.onTrack ? 'rgba(63,185,80,0.08)' : p.pct >= 20 ? 'rgba(210,153,34,0.08)' : 'rgba(248,81,73,0.08)',
          border: `1px solid ${p.onTrack ? 'rgba(63,185,80,0.3)' : p.pct >= 20 ? 'rgba(210,153,34,0.3)' : 'rgba(248,81,73,0.3)'}`,
          borderRadius: '12px',
          padding: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {p.onTrack
                ? <CheckCircle style={{ width: '28px', height: '28px', color: '#3fb950' }} />
                : <Target style={{ width: '28px', height: '28px', color: p.pct >= 20 ? '#d29922' : '#f85149' }} />
              }
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#e6edf3', margin: 0 }}>
                  {p.onTrack ? 'Monthly Target On Track' : p.pct >= 20 ? 'Monthly Target - Recovery Needed' : 'Monthly Target - Critical'}
                </h3>
                <p style={{ color: '#8b949e', fontSize: '13px', margin: '2px 0 0' }}>
                  <span style={{ color: '#e6edf3', fontWeight: 600 }}>${numberWithCommas(p.target)}</span> target for {new Date().toLocaleDateString('en-US', { month: 'long' })} &middot; Day {p.daysPassed} of {p.daysInMonth}
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: p.onTrack ? '#3fb950' : p.pct >= 20 ? '#d29922' : '#f85149' }}>
                {p.pct.toFixed(1)}%
              </div>
              <div style={{ fontSize: '12px', color: '#8b949e' }}>{p.onTrack ? 'on track' : 'of target'}</div>
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ background: '#21262d', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '6px', transition: 'width 0.5s',
                width: `${Math.min(100, p.pct)}%`,
                background: p.onTrack ? '#3fb950' : p.pct >= 20 ? '#d29922' : '#f85149'
              }} />
            </div>
            <div style={{ position: 'absolute', left: `${Math.min(100, (p.daysPassed / p.daysInMonth) * 100)}%`, top: '-3px', height: '16px', width: '2px', background: '#58a6ff', borderRadius: '1px' }} title="Expected position" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '13px' }}>
            <span style={{ color: '#8b949e' }}>
              Earned: <span style={{ color: '#3fb950', fontWeight: 600 }}>${numberWithCommas(p.rev.toFixed(0))}</span>
            </span>
            <span style={{ color: '#8b949e' }}>
              {p.onTrack ? 'Ahead' : 'Behind'}: <span style={{ color: p.onTrack ? '#3fb950' : '#f85149', fontWeight: 600 }}>
                ${numberWithCommas(Math.abs(p.rev - p.shouldHave).toFixed(0))}
              </span>
            </span>
            <span style={{ color: '#8b949e' }}>
              Target: <span style={{ color: '#e6edf3', fontWeight: 600 }}>${numberWithCommas(p.target)}</span>
            </span>
          </div>

          {/* Key metrics - labeled boxes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '14px' }}>
            <div style={{ background: '#0d1117', borderRadius: '8px', padding: '10px 12px', border: '1px solid #21262d' }}>
              <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>Avg Daily Rate</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#a855f7' }}>${numberWithCommas(p.dailyRate.toFixed(0))}</div>
            </div>
            {!p.onTrack && (
              <div style={{ background: 'rgba(249,115,22,0.08)', borderRadius: '8px', padding: '10px 12px', border: '1px solid rgba(249,115,22,0.2)' }}>
                <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>Daily Needed ({p.daysLeft}d left)</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#f97316' }}>${numberWithCommas(p.dailyNeeded.toFixed(0))}</div>
              </div>
            )}
            <div style={{ background: p.estimated >= p.target ? 'rgba(63,185,80,0.08)' : 'rgba(248,81,73,0.08)', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${p.estimated >= p.target ? 'rgba(63,185,80,0.2)' : 'rgba(248,81,73,0.2)'}` }}>
              <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>Estimated End-of-Month</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: p.estimated >= p.target ? '#3fb950' : '#f85149' }}>${numberWithCommas(p.estimated.toFixed(0))}</div>
            </div>
            <div style={{ background: p.estimated >= p.target ? 'rgba(63,185,80,0.08)' : 'rgba(248,81,73,0.08)', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${p.estimated >= p.target ? 'rgba(63,185,80,0.2)' : 'rgba(248,81,73,0.2)'}` }}>
              <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>{p.estimated >= p.target ? 'Over Target By' : 'Short of Target By'}</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: p.estimated >= p.target ? '#3fb950' : '#f85149' }}>${numberWithCommas(Math.abs(p.estimated - p.target).toFixed(0))}</div>
            </div>
          </div>

          {!p.onTrack && (
            <div className="alert-danger" style={{ marginTop: '14px' }}>
              <AlertCircle />
              <span>
                If we don't make at least <strong>${numberWithCommas(p.dailyNeeded.toFixed(0))}/day</strong> for the next {p.daysLeft} days, we'll miss the ${numberWithCommas(p.target)} target.
                At the current pace of ${numberWithCommas(p.dailyRate.toFixed(0))}/day, we'll end at <strong>${numberWithCommas(p.estimated.toFixed(0))}</strong> — <strong>${numberWithCommas((p.target - p.estimated).toFixed(0))} short</strong>.
              </span>
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="stats-grid">
          <StatCard title="Daily Revenue" value={`$${numberWithCommas(d.revt || 0)}`} icon={DollarSign}
            change={chg(d.revenue, d.revt)} color="green" isLoading={!d.revenue}
            subtitle={p.dailyNeeded > 0 ? `${dailyPct.toFixed(0)}% of daily target ($${numberWithCommas(p.dailyNeeded.toFixed(0))})` : undefined}
            progress={dailyPct} />
          <StatCard title="Daily Withdrawals" value={`$${numberWithCommas(d.withT || 0)}`} icon={TrendingDown}
            change={chg(d.spendings, d.withT)} color="red" isLoading={!d.spendings} />
          <StatCard title="Monthly Revenue" value={`$${numberWithCommas(d.monthlyRevThis || 0)}`} icon={Layers}
            change={d.monthlyRev?.length > 1 ? ((d.monthlyRev[d.monthlyRev.length - 1] / (d.monthlyRev[d.monthlyRev.length - 2] || 1)) - 1) * 100 : 0}
            color="blue" isLoading={!d.monthlyRev} subtitle={`${p.pct.toFixed(1)}% of $${numberWithCommas(p.target)} target`}
            progress={p.pct} />
          <StatCard title="Active Users Today" value={d.activeUsers ? numberWithCommas(d.activeUsers[d.activeUsers.length - 1] || 0) : '0'}
            icon={Users} change={d.activeUsers?.length > 1 ? ((d.activeUsers[d.activeUsers.length - 1] / (d.activeUsers[d.activeUsers.length - 2] || 1)) - 1) * 100 : 0}
            color="purple" isLoading={!d.activeUsers} />
        </div>

        {/* Revenue Table */}
        <div className="sections-grid">
          <DataTable icon={DollarSign} iconColor="#3fb950" title="Revenue vs Withdrawals (Last 10 Days)"
            headers={['Date', { label: 'Revenue', align: 'right' }, { label: 'Withdrawals', align: 'right' }, { label: 'Profits', align: 'right' }, { label: 'Margin %', align: 'right' }]}
            data={revenueRows} skeletonRows={10}
            renderRow={(row, i) => (
              <tr key={i}>
                <td className="cell-primary">{row.date} {i === 0 ? '(Today)' : ''}</td>
                <td className="text-right cell-green">${numberWithCommas(parseFloat(row.revenue).toFixed(2))}</td>
                <td className="text-right cell-red">${numberWithCommas(parseFloat(row.spendings).toFixed(2))}</td>
                <td className="text-right cell-blue">${numberWithCommas(parseFloat(row.profits).toFixed(2))}</td>
                <td className="text-right cell-mono" style={{ color: '#c9d1d9' }}>{row.margin.toFixed(1)}%</td>
              </tr>
            )}
          />

          {/* Active Users Chart */}
          {chartData && (
            <div className="section-card">
              <h3 className="section-title"><Users style={{ color: '#58a6ff' }} /> Active Users (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4158D0" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#4158D0" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                  <XAxis dataKey="date" stroke="#484f58" fontSize={12} />
                  <YAxis stroke="#484f58" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: '#161b22', border: '1px solid #30363d', borderRadius: '8px', color: '#e6edf3' }}
                    formatter={(v) => [fmt(v), 'Users']} />
                  <Area type="monotone" dataKey="users" stroke="#4F46E5" fillOpacity={1} fill="url(#colorUsers)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly Revenue */}
          <DataTable icon={TrendingUp} iconColor="#a855f7" title="Monthly Revenue & Target (Last 12 Months)"
            headers={['Month', { label: 'Revenue', align: 'right' }, { label: 'Target', align: 'right' }, { label: 'Achievement %', align: 'right' }, { label: 'Difference', align: 'right' }]}
            data={monthlyRows} skeletonRows={12}
            renderRow={(row, i) => (
              <tr key={i}>
                <td className="cell-primary">{row.month} {i === 0 ? '(Current)' : ''}</td>
                <td className="text-right cell-green">${numberWithCommas(parseFloat(row.revenue).toFixed(0))}</td>
                <td className="text-right cell-muted">${numberWithCommas(parseFloat(row.target).toFixed(0))}</td>
                <td className={`text-right ${pctColor(row.achievement)}`}>{row.achievement}%</td>
                <td className={`text-right ${row.diff >= 0 ? 'cell-green' : 'cell-red'}`}>
                  {row.diff >= 0 ? '+' : ''}${numberWithCommas(row.diff.toFixed(0))}
                </td>
              </tr>
            )}
          />

          {/* Walls */}
          <DataTable icon={Activity} iconColor="#f97316" title="Wall Earnings & Targets"
            headers={['Wall Name', { label: "Today's Earnings", align: 'right' }, { label: 'Today %', align: 'right' }, { label: 'Monthly Earnings', align: 'right' }, { label: 'Monthly %', align: 'right' }]}
            data={wallRows}
            renderRow={(w, i) => (
              <tr key={i}>
                <td className="cell-primary">{w.name}</td>
                <td className="text-right cell-green">${numberWithCommas(w.today.toFixed(2))}</td>
                <td className={`text-right ${pctColor(parseFloat(w.todayPct))}`}>{w.todayPct}%</td>
                <td className="text-right cell-blue">${numberWithCommas(w.monthly.toFixed(2))}</td>
                <td className={`text-right ${pctColor(parseFloat(w.monthlyPct))}`}>{w.monthlyPct}%</td>
              </tr>
            )}
          />
        </div>

        {/* Top & Last Offers */}
        <div className="two-col-grid">
          <DataTable icon={Gamepad} iconColor="#14b8a6" title="Top 10 Offers Made Today"
            headers={['Name', 'Wall', { label: 'Count', align: 'right' }]}
            data={d.top10Offers} skeletonRows={10}
            renderRow={(o, i) => (
              <tr key={i}>
                <td>{o.name}</td>
                <td>{o.wall_name}</td>
                <td className="text-right">{o.c}</td>
              </tr>
            )}
          />
          <DataTable icon={Gamepad} iconColor="#14b8a6" title="Last 10 Offers Made Today"
            headers={['Name', 'Wall', { label: 'Country', align: 'right' }]}
            data={d.last10Offers} skeletonRows={10}
            renderRow={(o, i) => (
              <tr key={i}>
                <td>{o.name}</td>
                <td>{o.wall}</td>
                <td className="text-right">{o.country}</td>
              </tr>
            )}
          />
           <DataTable icon={Gamepad} iconColor="#14b8a6" title="Top Offers Last Hours"
            headers={['Name', 'Wall','Reward', { label: 'Country', align: 'right' }]}
            data={d.last10OffersHours} skeletonRows={10}
            renderRow={(o, i) => (
              <tr key={i}>
                <td>{o.name}</td>
                <td>{o.wall}</td>
                <td>{o.reward}</td>
                <td className="text-right">{o.country}</td>
              </tr>
            )}
          />
        </div>

        {/* Last 10 Users */}
        <DataTable icon={Users2} iconColor="#a855f7" title="Last 10 Accounts Created"
          headers={['ID', 'Balance', 'Name', 'Email Provider', { label: 'Country', align: 'right' }]}
          data={d.last10Users} skeletonRows={10}
          renderRow={(u, i) => (
            <tr key={i}>
              <td className="cell-mono"><Link to={`/pluto/user/${u.id}`} style={{ color: '#58a6ff' }}>{u.id}</Link></td>
              <td className="cell-green">${numberWithCommas(u.balance)}</td>
              <td>{u.name}</td>
              <td>{u.email_provider}</td>
              <td className="text-right">{u.country}</td>
            </tr>
          )}
        />
      </div>
    </div>
  )
}

export default PlutoMain
